import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";
import { audioEngine } from "$/modules/audio/audio-engine";
import { audioBufferAtom, currentTimeAtom } from "$/modules/audio/states";

export function useScrubbing(
	scrollContainerRef: React.RefObject<HTMLDivElement | null>,
	scrollLeft: number,
	zoom: number,
) {
	const audioBuffer = useAtomValue(audioBufferAtom);
	const setCurrentTime = useSetAtom(currentTimeAtom);
	const isScrubbingRef = useRef(false);
	const scrollLeftForScrubRef = useRef(scrollLeft);
	const currentMouseXRef = useRef(0);

	const handleScrubMove = useCallback(
		(event: MouseEvent) => {
			if (!scrollContainerRef.current || !audioBuffer) return;

			const rect = scrollContainerRef.current.getBoundingClientRect();
			const mouseX = event.clientX - rect.left;
			currentMouseXRef.current = mouseX;

			const clampedMouseX = Math.max(0, Math.min(mouseX, rect.width));

			const timeAtCursor =
				(scrollLeftForScrubRef.current + clampedMouseX) / zoom;

			const clampedTime = Math.max(
				0,
				Math.min(timeAtCursor, audioBuffer.duration),
			);

			audioEngine.seekMusic(clampedTime);
			setCurrentTime(clampedTime * 1000);
		},
		[audioBuffer, zoom, setCurrentTime, scrollContainerRef],
	);

	const handleScrubEnd = useCallback(() => {
		isScrubbingRef.current = false;
		window.removeEventListener("mousemove", handleScrubMove);
		window.removeEventListener("mouseup", handleScrubEnd);
	}, [handleScrubMove]);

	useEffect(() => handleScrubEnd, [handleScrubEnd]);

	const handleScrubStart = useCallback(
		(event: React.MouseEvent) => {
			if (event.button !== 0) return;
			event.preventDefault();
			isScrubbingRef.current = true;
			scrollLeftForScrubRef.current = scrollLeft;

			handleScrubMove(event.nativeEvent);

			window.addEventListener("mousemove", handleScrubMove);
			window.addEventListener("mouseup", handleScrubEnd, { once: true });
		},
		[handleScrubMove, handleScrubEnd, scrollLeft],
	);

	return { handleScrubStart };
}
